import React, { useEffect, useState } from "react";
import { DebounceInput } from "react-debounce-input";
import { toast } from "react-hot-toast";
import { FaPlus } from "react-icons/fa";
import { HiOutlineDocumentText } from "react-icons/hi2";

import Loader from "@/components/loader";
import Pagination from "@/components/pagination";
import api from "@/services/api";
import ResourceCard from "./components/ResourceCard";
import EditResourceModal from "./modals/EditResourceModal";

export default function Resources() {
  const [search, setSearch] = useState("");
  const [refresh, setRefesh] = useState(0);
  const [filterType, setFilterType] = useState("");
  const [selectedTags, setSelectedTags] = useState([]);

  const commonTags = ["Tech", "Product", "Leadership", "Admin"];

  const toggleTag = (tag) => {
    setSelectedTags((prev) => {
      if (prev.includes(tag)) {
        return prev.filter((t) => t !== tag);
      } else {
        return [...prev, tag];
      }
    });
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex justify-between items-start mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Resources & Inspiration</h1>
          <p className="text-gray-600 mt-1">Curated books, articles, videos, and tutorials to help you grow.</p>
        </div>
        <CreateResourceButton onCreated={() => setRefesh((prev) => prev + 1)} />
      </div>

      <div className="mb-8 space-y-4">
        <DebounceInput
          debounceTimeout={500}
          className="w-full max-w-md px-4 py-2 rounded-xl border border-gray-200 focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all outline-none"
          placeholder="Search resources..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        
        <div className="space-y-3">
          <div className="flex flex-wrap gap-2">
            <FilterButton active={filterType === ""} onClick={() => setFilterType("")}>All</FilterButton>
            <FilterButton active={filterType === "tutorial"} onClick={() => setFilterType("tutorial")}>Tutorials</FilterButton>
            <FilterButton active={filterType === "component"} onClick={() => setFilterType("component")}>Components</FilterButton>
            <FilterButton active={filterType === "book"} onClick={() => setFilterType("book")}>Books</FilterButton>
            <FilterButton active={filterType === "article"} onClick={() => setFilterType("article")}>Articles</FilterButton>
            <FilterButton active={filterType === "video"} onClick={() => setFilterType("video")}>Videos</FilterButton>
            <FilterButton active={filterType === "newsletter"} onClick={() => setFilterType("newsletter")}>Newsletters</FilterButton>
          </div>
          
          <div className="flex flex-wrap gap-2">
            {commonTags.map((tag) => (
              <FilterButton
                key={tag}
                active={selectedTags.includes(tag)}
                onClick={() => toggleTag(tag)}
              >
                {tag}
              </FilterButton>
            ))}
          </div>
        </div>
      </div>

      <ResourcesList 
        search={search} 
        refresh={refresh} 
        filterType={filterType} 
        selectedTags={selectedTags}
        onUpdate={() => setRefesh((prev) => prev + 1)} 
      />
    </div>
  );
}

const FilterButton = ({ children, active, onClick }) => (
  <button
    onClick={onClick}
    className={`px-3 py-1.5 text-sm font-medium rounded-full transition-colors ${
      active 
        ? "bg-gray-900 text-white" 
        : "bg-gray-100 text-gray-600 hover:bg-gray-200"
    }`}>
    {children}
  </button>
);

function ResourcesList({ search, refresh, filterType, selectedTags, onUpdate }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const perPage = 20;

  useEffect(() => {
    setPage(1); // Reset to page 1 when search or filter changes
  }, [search, filterType, selectedTags]);

  useEffect(() => {
    fetchAll();
  }, [search, refresh, filterType, selectedTags, page]);

  const fetchAll = async () => {
    setLoading(true);
    try {
      // Use workshop endpoint - normalize data structure
      const { data, ok, total: totalCount } = await api.post("/workshop/search", {
        search: search,
        type: filterType || undefined,
        tags: selectedTags.length > 0 ? selectedTags : undefined,
        page: page,
        per_page: perPage
      });

      if (!ok) {
        toast.error("Failed to fetch content");
        return;
      }

      // Normalize workshop data to resource format (name -> title, etc.)
      const normalizedData = (data || []).map(item => ({
        ...item,
        title: item.name || item.title, // Use name as title
        name: item.name, // Keep original name too
        description: item.description || "",
        image: item.image || null,
        author: item.author || null,
        url: item.url || null,
        tags: item.tags || [],
        type: item.type || "other",
        level: item.level || null,
        axe: item.axe || null,
        category: item.category || null,
        _id: item._id,
        updatedAt: item.updated_at || item.created_at,
        createdAt: item.created_at,
        // Component-specific fields
        component_id: item.component_id || null,
        component_images: item.component_images || [],
        features: item.features || [],
        views: item.views || 0,
        github_url: item.github_url || null,
        figma_url: item.figma_url || null,
      }));

      setItems(normalizedData);
      setTotal(totalCount || 0);
    } catch (error) {
      console.error(error);
      toast.error("Failed to fetch content");
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <Loader />;

  if (items.length === 0) {
    return (
      <div className="text-center py-12 bg-white rounded-2xl border border-gray-200">
        <HiOutlineDocumentText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
        <h3 className="text-lg font-medium text-gray-900">No resources found</h3>
        <p className="text-gray-500">Try adjusting your filters or search</p>
      </div>
    );
  }

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {items.map((item) => (
          <ResourceCard key={item._id || item.id} resource={item} onUpdate={onUpdate} />
        ))}
      </div>
      
      {total > perPage && (
        <div className="mt-8">
          <Pagination
            total={total}
            per_page={perPage}
            currentPage={page}
            onNext={() => setPage((prev) => prev + 1)}
            onPrevious={() => setPage((prev) => Math.max(1, prev - 1))}
          />
        </div>
      )}
    </>
  );
}

function CreateResourceButton({ onCreated }) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <>
      <button
        onClick={() => setIsOpen(true)}
        className="flex items-center gap-2 bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-xl font-medium transition-colors shadow-sm">
        <FaPlus className="w-3 h-3" />
        Add Resource
      </button>

      <EditResourceModal onClose={() => setIsOpen(false)} onUpdated={onCreated} isOpen={isOpen} />
    </>
  );
}

